<template>
  <div class="address-autocomplete">
    <input
      ref="addressInput"
      type="text"
      v-model="inputValue"
      :placeholder="placeholder"
      @blur="handleBlur"
      @input="handleInput"
      class="address-input"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import { loadGoogleMapsAPI } from '@/main.js'

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: 'Enter address...'
  }
})

const emit = defineEmits(['update:modelValue', 'place-selected'])

const addressInput = ref(null)
const inputValue = ref(props.modelValue)
let autocomplete = null

// Watch for external changes to modelValue
watch(() => props.modelValue, (newValue) => {
  inputValue.value = newValue
})

// Watch for internal changes to inputValue
watch(inputValue, (newValue) => {
  emit('update:modelValue', newValue)
})

onMounted(async () => {
  try {
    // Wait for Google Maps API to load
    await loadGoogleMapsAPI
    
    // Initialize Google Places Autocomplete
    autocomplete = new google.maps.places.Autocomplete(addressInput.value, {
      types: ['address'],
      componentRestrictions: { country: 'us' }, // Restrict to US addresses
      fields: ['formatted_address', 'address_components', 'geometry']
    })
    
    // Listen for place selection
    autocomplete.addListener('place_changed', handlePlaceSelect)
    
    console.log('✅ Google Places Autocomplete initialized')
  } catch (error) {
    console.error('❌ Failed to initialize Google Places Autocomplete:', error)
  }
})

function handlePlaceSelect() {
  const place = autocomplete.getPlace()
  
  // Add comprehensive null checks
  if (!place || !place.address_components) {
    console.log('⚠️ No valid place or address components found')
    return
  }
  
  // Extract address components
  const addressComponents = {}
  place.address_components.forEach(component => {
    const types = component.types
    if (types.includes('street_number')) {
      addressComponents.street_number = component.long_name
    }
    if (types.includes('route')) {
      addressComponents.route = component.long_name
    }
    if (types.includes('locality')) {
      addressComponents.city = component.long_name
    }
    if (types.includes('administrative_area_level_1')) {
      addressComponents.state = component.short_name
    }
    if (types.includes('postal_code')) {
      addressComponents.zip = component.long_name
    }
  })
  
  // Build full street address
  const streetAddress = [
    addressComponents.street_number,
    addressComponents.route
  ].filter(Boolean).join(' ')
  
  // Update input value
  inputValue.value = streetAddress
  
  // Emit place selection event with all components
  emit('place-selected', {
    streetAddress,
    city: addressComponents.city || '',
    state: addressComponents.state || '',
    zip: addressComponents.zip || '',
    fullAddress: place.formatted_address,
    coordinates: place.geometry ? {
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng()
    } : null
  })
  
  console.log('📍 Address selected:', {
    streetAddress,
    city: addressComponents.city,
    state: addressComponents.state,
    zip: addressComponents.zip
  })
}

function handleInput() {
  // Clear the place selection when user types manually
  if (autocomplete) {
    autocomplete.set('place', null)
  }
}

function handleBlur() {
  // Optional: Handle blur event if needed
}
</script>

<style scoped>
.address-autocomplete {
  width: 100%;
}

.address-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.3s ease;
}

.address-input:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.address-input::placeholder {
  color: #999;
}
</style> 